import type { DefectRouteStep } from '../pms/condition-types.js';
import type { DefectSummary } from './types.js';

/**
 * Arızanın akışı ve kapanış onay yolu (ADR 0091): gemide Open → Investigating → Repair → Testing →
 * Verification, sonra kapanış onayı (ör. Master gemide → Superintendent ofiste) ve Closed. Ofis
 * onayı yalnız kapanıştadır; ortağın "ofis nerede devreye giriyor" sorusunun cevabı ekranda yazar.
 */

/** Gemide yürüyen durumlar, sırayla (ADR 0060 §10). */
export const FLOW_STATUSES = [
  'open',
  'investigating',
  'repair',
  'testing',
  'verification',
] as const;

export interface FlowItem {
  /** Durum, `closure` (kapanış onayı) ya da `closed`. */
  key: string;
  state: 'done' | 'current' | 'next';
}

type Detail = Pick<DefectSummary, 'status' | 'closure'> & { ownRecord?: boolean };

/** Şerit: bulunulan aşama `current`, öncekiler `done`. */
export function closureFlow(detail: Detail): FlowItem[] {
  const keys = [...FLOW_STATUSES, 'closure', 'closed'];
  const current = detail.closure.closed
    ? keys.length
    : detail.closure.status === 'pending'
      ? keys.indexOf('closure')
      : Math.max(0, keys.indexOf(String(detail.status)));
  return keys.map((key, index) => ({
    key,
    state: index < current ? 'done' : index === current ? 'current' : 'next',
  }));
}

/** Onay zinciri: ofis adımına katılan gemi adımı ayrı halka değildir (ADR 0091). */
export function routeChain(
  route: readonly DefectRouteStep[],
): { roleName: string | null; office: boolean }[] {
  return route
    .filter((step) => step.escalation?.to !== 'merged')
    .map((step) => ({ roleName: step.roleName, office: step.office }));
}

export interface ClosureNote {
  key:
    | 'officeAtClosure'
    | 'closureOnBoard'
    | 'escalated'
    | 'ownEscalated'
    | 'awaitingShip'
    | 'awaitingOffice'
    | 'decideHere'
    | 'decideByRole'
    | 'decidedOnBoard'
    | 'ownWaiting'
    | 'officeWaiting';
  params: Record<string, string>;
}

/** Zincirin metni: "Master (on board) → Superintendent (office)". */
export function chainText(
  route: readonly DefectRouteStep[],
  where: (office: boolean) => string,
): string {
  return routeChain(route)
    .map((step) => `${step.roleName ?? '—'} (${where(step.office)})`)
    .join(' → ');
}

/**
 * Kapanışın notları: gönderilmemiş arızada onayın kapanışta geldiği ve zinciri; bekleyen kapanışta
 * kimin onayı beklendiği ve kararın nerede verildiği; kendi kaydını onaylayamayan onaylayıcının
 * adımı ofise geçtiyse nedeni (gemide gönderen kişiye "siz gönderdiniz").
 * @param decidable Bakan kişi bekleyen adıma burada karar verebilir mi.
 */
export function closureNotes(
  detail: Detail,
  input: { scope: 'ship' | 'office'; decidable: boolean },
  where: (office: boolean) => string = (office) => (office ? 'office' : 'on board'),
): ClosureNote[] {
  const route = detail.closure.route ?? [];
  if (detail.closure.closed) return [];
  const notes: ClosureNote[] = [];
  const escalated = route.find((step) => step.escalation);
  if (escalated?.escalation && detail.closure.status === 'pending') {
    const officeStep =
      escalated.escalation.to === 'office'
        ? escalated
        : (route.find((step) => step.office && !step.escalation) ?? escalated);
    notes.push({
      key: input.scope === 'ship' && detail.ownRecord ? 'ownEscalated' : 'escalated',
      params: {
        role: officeStep.roleName ?? '—',
        shipRole: escalated.escalation.shipRoleName ?? '—',
      },
    });
  }
  if (detail.closure.status !== 'pending') {
    if (route.length > 0) {
      notes.push({
        key: route.some((step) => step.office) ? 'officeAtClosure' : 'closureOnBoard',
        params: { chain: chainText(route, where) },
      });
    }
    return notes;
  }
  // Eski sunucu bekleyen rolün adını göndermez: not yazılmaz
  if (detail.closure.approverRoleName === undefined) return notes;
  const role = { role: detail.closure.approverRoleName ?? '—' };
  if (detail.closure.office) {
    notes.push({ key: 'awaitingOffice', params: role });
    if (input.scope === 'office') {
      notes.push(
        input.decidable ? { key: 'decideHere', params: {} } : { key: 'decideByRole', params: role },
      );
    } else {
      notes.push({ key: 'officeWaiting', params: {} });
    }
    return notes;
  }
  notes.push({ key: 'awaitingShip', params: role });
  if (input.scope === 'office') notes.push({ key: 'decidedOnBoard', params: role });
  else if (input.decidable) notes.push({ key: 'decideHere', params: {} });
  else if (detail.ownRecord) notes.push({ key: 'ownWaiting', params: role });
  return notes;
}
